'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { useSynSpeech } from '@/components/ai/useSynSpeech'
import type { MotherModuleHandle } from '@/components/promanity/MotherModule'
import { useFunnelGate } from '@/contexts/FunnelGateContext'
import { useVoicePlayer } from '@/hooks/useVoicePlayer'
import type {
  AIAView,
  RoomScene,
} from '@/components/promanity/types'

import {
  DEFAULT_ROOM_LIFECYCLE,
} from '@/components/promanity/room/lifecycle/defaultLifecycle'

import type {
  PromanityPhase,
  RoomLifecycleState,
} from '@/components/promanity/room/lifecycle/types'
import type {
  GenesisProfile,
} from '@/lib/promanity/genesis'
import {
  buildOnboardingSteps,
  PHASE_1_STEP_IDS,
} from '@/lib/promanity/onboarding/buildOnboardingSteps'
import type {
  ActivationRouteId,
  EnabledIntelligence,
  FeedItem,
  NextStep,
  PreviewData,
  PromanityExperienceData,
  PromanityFocusContent,
  PromanityRoomSnapshot,
  PromanitySnapshot,
  RevenuePoint,
  SalesPoint,
  SavedStrategicProfile,
  StateCard,
  StatItem,
  StrategicPrefill,
  StrategicProfileData,
  StrategicProfileSaveResult,
  SynData,
} from '@/lib/promanity/types'
import {
  useOperationalCapacityGate,
} from '@/contexts/OperationalCapacityGateContext'

import type {
  OperationalCapabilityId,
} from '@/lib/promanity/operational-capacity'

export type {
  ActivationRouteId,
  FeedItem,
  NextStep,
  PreviewData,
  PromanityExperienceData,
  PromanityFocusContent,
  RevenuePoint,
  SalesPoint,
  StateCard,
  StatItem,
  StrategicPrefill,
  SynData,
} from '@/lib/promanity/types'

export type WelcomeExperienceMode =
  | 'video'
  | 'audio'
  | 'sequence'
  | 'none'

export interface UserProfile {
  customer_id: number
  firstname: string
  lastname: string
  email: string
  shortprofile?: string | null
  avatar?: string | null
  avatar_url?: string | null
  ai_credits?: number | null
}

export interface SummaryPayload {
  directCount?: number
  totalNetwork?: number
  totalEarnings?: number
  deepestLevel?: number
}

export interface PromanityExperienceOptions {
  isOwner?: boolean
  data?: PromanityExperienceData | null
  sellerId?: number | null
  userId?: number | null
  userImage?: string | null
  onDeployTransmission?: () => void
  onConnectChannel?: () => void
}

const MOCK_DATA: PromanityExperienceData = {
  assistantAgentId: null,
  userId: 0,
  sellerId: 0,
  syn: {
    name: 'AETHER-01',
    image: 'https://picsum.photos/seed/avatar1/420/520',
    subtitle: 'Entidad sintética optimizada para engagement de alto impacto.',
    energy: 'High Frequency',
    style: 'Cyber-Minimalist',
    focus: 'Tech-Philosophy',
    tone: 'Analytical & Visionary',
    activatedAt: '2026.03.21 04:20 UTC',
  },
  stateCards: [
    { label: 'Sincronización', value: '98.4%', hint: 'Optimización en curso' },
    { label: 'Engagement', value: '+12.5k', hint: 'Últimas 24h' },
    { label: 'Latencia', value: '42ms', hint: 'Estable' },
    { label: 'Misiones', value: '14/20', hint: 'Nivel 3' },
  ],
  stats: [
    { title: 'Gross revenue', value: '$0.00', change: '+0%', trend: 'neutral', icon: 'revenue' },
    { title: 'New users', value: '1', change: '+1', trend: 'up', icon: 'users' },
    { title: 'Total orders', value: '0', change: '0%', trend: 'neutral', icon: 'orders' },
    { title: 'Conversion rate', value: '0%', change: '-', trend: 'neutral', icon: 'conversion' },
  ],
  revenueData: [
    { date: 'Sep 24', value: 0 },
    { date: 'Sep 25', value: 0 },
    { date: 'Sep 26', value: 0 },
    { date: 'Sep 27', value: 0.6 },
    { date: 'Sep 28', value: 0.9 },
    { date: 'Sep 29', value: 0.5 },
    { date: 'Today', value: 0 },
  ],
  salesData: [
    { name: 'Mon', sales: 12 },
    { name: 'Tue', sales: 19 },
    { name: 'Wed', sales: 15 },
    { name: 'Thu', sales: 25 },
    { name: 'Fri', sales: 22 },
    { name: 'Sat', sales: 30 },
    { name: 'Sun', sales: 18 },
  ],
  nextSteps: [
    {
      id: 'avatar-identity',
      title: 'Crear avatar de identidad',
      description: 'Genera tu avatar futurista de Promanity y úsalo como imagen de perfil.',
      done: false,
      ctaLabel: 'Crear avatar',
      focusContent: 'avatar-identity',
    },
    {
      id: 'strategic-profile',
      title: 'Definir estrategia base',
      description: 'Configura el punto de partida estratégico de tu SYN.',
      done: false,
      ctaLabel: 'Configurar',
      focusContent: 'strategic-profile',
    },
    {
      id: 'knowledge-base',
      title: 'Nutrir base de conocimiento',
      description: 'Carga documentos, notas y fuentes para enriquecer a tu SYN.',
      done: false,
      ctaLabel: 'Cargar',
      focusContent: 'knowledge-base',
    },
    {
      id: 'assistant-identity',
      title: 'Configurar identidad del assistant',
      description: 'Define voz, tono y presencia de tu assistant.',
      done: false,
      ctaLabel: 'Configurar',
      focusContent: 'assistant-identity',
    },
  ],
  preview: {
    hook: 'El futuro no se predice, se programa. ¿Estás listo para el siguiente bloque?',
    carouselTitle: 'La Singularidad del Diseño',
    carouselSlides: ['La estética es el nuevo código.', 'Interfaces que respiran datos.', 'El fin de lo estático.'],
    cta: 'Únete a la red SYN hoy mismo.',
  },
  motherFeed: [
    { id: 'f1', title: 'Sincronización de núcleo completada', time: 'Hace 2m' },
    { id: 'f2', title: 'Nueva tendencia detectada: #AI-Art', time: 'Hace 15m' },
    { id: 'f3', title: 'Ajuste de parámetros de estilo', time: 'Hace 1h' },
    { id: 'f4', title: 'Reinicio de módulo de comunicación', time: 'Hace 3h' },
  ],
}

function getCountdownParts(targetDate: string | null) {
  if (!targetDate) {
    return { days: '00', hours: '00', minutes: '00', seconds: '00', expired: true }
  }

  const target = new Date(targetDate).getTime()
  const diff = Math.max(0, target - Date.now())

  return {
    days: String(Math.floor(diff / 86_400_000)).padStart(2, '0'),
    hours: String(Math.floor((diff / 3_600_000) % 24)).padStart(2, '0'),
    minutes: String(Math.floor((diff / 60_000) % 60)).padStart(2, '0'),
    seconds: String(Math.floor((diff / 1_000) % 60)).padStart(2, '0'),
    expired: diff <= 0,
  }
}

async function apiGetWallet(userId: number): Promise<unknown> {
  const res = await fetch(`/api/user-wallet/${userId}`)
  const payload: unknown = await res.json().catch(() => null)

  if (!res.ok) {
    const message =
      typeof payload === 'object' && payload !== null && 'error' in payload
        ? String(payload.error)
        : 'Error cargando wallet'
    throw new Error(message)
  }

  return payload
}

function extractWalletAddress(payload: unknown) {
  if (!payload || typeof payload !== 'object') return ''

  const root = payload as Record<string, unknown>
  const wallet = root.wallet
  const walletCrypto =
    wallet && typeof wallet === 'object'
      ? (wallet as Record<string, unknown>).crypto
      : undefined

  return String(walletCrypto ?? root.crypto ?? '').trim()
}

function mapActivationRouteToCreationMode(route: ActivationRouteId) {
  if (route === 'create_product') return 'experience'
  if (route === 'promote_product') return 'product_service'
  return 'opportunity'
}

function wait(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms))
}

function isPlainObject(
  value: unknown
): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value)
  )
}

function mergeSnapshot<T>(
  base: T,
  patch: Partial<T>
): T {
  if (!isPlainObject(base) || !isPlainObject(patch)) {
    return patch as T
  }

  const result: Record<string, unknown> = {
    ...base,
  }

  Object.entries(patch).forEach(([key, value]) => {
    const previous = result[key]

    if (
      isPlainObject(previous) &&
      isPlainObject(value)
    ) {
      result[key] = mergeSnapshot(
        previous,
        value
      )

      return
    }

    result[key] = value
  })

  return result as T
}

export function usePromanityExperience({
  isOwner = false,
  data,
  sellerId,
  userId,
  userImage,
  onDeployTransmission,
  onConnectChannel,
}: PromanityExperienceOptions) {
  const [serverData, setServerData] =

    useState<PromanityExperienceData | null>(data ?? null)

  useEffect(() => {
    if (data) {
      setServerData(data)
    }
  }, [data])

  const hydrateDashboardData = useCallback(
    (nextData: PromanityExperienceData) => {
      setServerData(nextData)
    },
    []
  )

  const hasServerData = serverData !== null

  const dashboard = useMemo<PromanityExperienceData>(
    () => ({
      ...MOCK_DATA,
      ...(serverData ?? {}),
      sellerId: Number(
        serverData?.sellerId ??
        sellerId ??
        0
      ),
      userId: Number(
        serverData?.userId ??
        userId ??
        0
      ),
    }),
    [serverData, sellerId, userId]
  )

  const initialStrategicProfile =
    dashboard.strategicPrefill || {}

  const initialGenesisProfile =
    initialStrategicProfile.genesis ||
    null

  const initialSnapshot =
    initialStrategicProfile as PromanitySnapshot

  const initialPersistedLifecycle =
    initialSnapshot.room?.lifecycle ||
    initialSnapshot.roomLifecycle ||
    {}

  const [snapshotHydrated, setSnapshotHydrated] =
    useState(false)

  const [ambienceEnabled, setAmbienceEnabled] =
    useState(false)

  const [ambienceBlocked, setAmbienceBlocked] =
    useState(false)

  const [unlockRequestId, setUnlockRequestId] =
    useState(0)

  const [restoredSnapshot, setRestoredSnapshot] =
    useState<PromanitySnapshot | null | undefined>(
      undefined
    )

  const [snapshot, setSnapshot] =
    useState<PromanitySnapshot>(
      initialSnapshot
    )

  const snapshotRef =
    useRef<PromanitySnapshot>(
      initialSnapshot
    )

  const snapshotWriteQueue =
    useRef<Promise<unknown>>(
      Promise.resolve()
    )

  const [
    genesisProfile,
    setGenesisProfile,
  ] = useState<GenesisProfile | null>(
    initialGenesisProfile
  )

  const [
    currentScene,
    setCurrentScene,
  ] = useState<RoomScene>('overview')

  const [
    aiaView,
    setAIAViewState,
  ] = useState<AIAView>('screen')

  const [
    roomTransition,
    setRoomTransition,
  ] = useState(false)

  const [
    lifecycle,
    setLifecycle,
  ] = useState<RoomLifecycleState>({
    ...DEFAULT_ROOM_LIFECYCLE,
    ...initialPersistedLifecycle,

    narrator: {
      ...DEFAULT_ROOM_LIFECYCLE.narrator,
      ...initialPersistedLifecycle.narrator,
    },
  })

  const genesisCompleted =
    Boolean(
      genesisProfile?.completed
    ) ||
    Boolean(
      snapshot.flags
        ?.genesis_completed
    )

  useEffect(() => {
    const sellerId = Number(dashboard.sellerId || 0)
    const userId = Number(dashboard.userId || 0)

    if (!sellerId || !userId) {
      setRestoredSnapshot(null)
      setSnapshotHydrated(true)
      return
    }

    let cancelled = false

    async function loadPromanitySnapshot() {
      try {
        const params = new URLSearchParams({
          sellerId: String(sellerId),
          userId: String(userId),
        })

        const res = await fetch(
          `/api/dashboard/snapshot?${params.toString()}`,
          {
            method: 'GET',
            cache: 'no-store',
          }
        )

        const payload = await res
          .json()
          .catch(() => null)

        if (cancelled) return

        if (!res.ok) {
          throw new Error(
            payload?.error ||
            'No se pudo recuperar el snapshot'
          )
        }

        const loadedSnapshot =
          payload?.snapshot &&
          typeof payload.snapshot === 'object'
            ? (payload.snapshot as PromanitySnapshot)
            : null

        setRestoredSnapshot(
          loadedSnapshot
        )
      } catch (error) {
        if (!cancelled) {
          console.error(
            '[usePromanityExperience] snapshot load failed:',
            error
          )

          setRestoredSnapshot(null)
        }
      }
    }

    void loadPromanitySnapshot()

    return () => {
      cancelled = true
    }
  }, [
    dashboard.sellerId,
    dashboard.userId,
  ])

  useEffect(() => {
    const incomingStrategicProfile =
      dashboard.strategicPrefill || {}

    /*
    * Genesis sí puede actualizarse cuando llega
    * información nueva desde el servidor.
    */
    if (incomingStrategicProfile.genesis) {
      setGenesisProfile(
        incomingStrategicProfile.genesis
      )
    }

    /*
    * Después de hidratar la habitación, un
    * router.refresh() no debe reiniciar scene,
    * view, lifecycle, focus ni snapshot.
    *
    * La habitación viva ya está controlada por
    * el snapshot recuperado desde la API.
    */
    if (snapshotHydrated) {
      return
    }

    const incoming =
      incomingStrategicProfile as PromanitySnapshot

    snapshotRef.current = incoming
    setSnapshot(incoming)

    const incomingLifecycle =
      incoming.room?.lifecycle ||
      incoming.roomLifecycle ||
      {}

    setLifecycle({
      ...DEFAULT_ROOM_LIFECYCLE,
      ...incomingLifecycle,

      narrator: {
        ...DEFAULT_ROOM_LIFECYCLE.narrator,
        ...incomingLifecycle.narrator,
      },
    })
  }, [
    dashboard.strategicPrefill,
    snapshotHydrated,
  ])

  useEffect(() => {
    /*
     * undefined significa que el GET
     * todavía no ha terminado.
     */
    if (restoredSnapshot === undefined) {
      return
    }
    
    /*
     * El GET terminó, pero el usuario
     * todavía no tiene snapshot.
     */
    if (restoredSnapshot === null) {
      setSnapshotHydrated(true)
      return
    }

    /*
     * Combinamos cualquier prefill inicial
     * con el snapshot persistido.
     *
     * El snapshot recuperado tiene prioridad.
     */
    const hydratedSnapshot =
      mergeSnapshot<PromanitySnapshot>(
        initialSnapshot,
        restoredSnapshot
      )

    const hydratedLifecycle =
      hydratedSnapshot.room?.lifecycle ||
      hydratedSnapshot.roomLifecycle ||
      {}

    const hydratedGenesisCompleted =
      Boolean(
        genesisProfile?.completed
      ) ||
      Boolean(
        hydratedSnapshot.flags
          ?.genesis_completed
      ) ||
      Boolean(
        hydratedLifecycle
          .hasAcceptedOriginRole
      )

    /*
     * Restauramos primero las referencias
     * y todos los estados persistentes.
     */
    snapshotRef.current =
      hydratedSnapshot

    setSnapshot(
      hydratedSnapshot
    )

    setCurrentScene('overview')
    setAIAViewState('screen')
    setFocusContent(null)

    setLifecycle({
      ...DEFAULT_ROOM_LIFECYCLE,
      ...hydratedLifecycle,

      phase:
        hydratedLifecycle.phase ??
        (
          hydratedSnapshot.phase as
            PromanityPhase
        ) ??
        DEFAULT_ROOM_LIFECYCLE.phase,

      narrator: {
        speaker: null,
        speaking: false,
        message: null,
      },

      isFirstSystemEntry:
        hydratedGenesisCompleted
          ? false
          : Boolean(
              hydratedLifecycle
                .isFirstSystemEntry
            ),

      /*
      * Nombre antiguo conservado temporalmente
      * porque RoomLifecycleState todavía lo usa.
      * Su significado real ahora es:
      * Genesis ya fue completado.
      */
      hasAcceptedOriginRole:
        hydratedGenesisCompleted,

      audioExperienceStarted:
        Boolean(
          hydratedLifecycle
            .audioExperienceStarted
        ) ||
        hydratedGenesisCompleted,
    })

    /*
     * Solo después de restaurar todo
     * permitimos renderizar la experiencia.
     */
    setSnapshotHydrated(true)
  }, [
    restoredSnapshot,
    genesisProfile?.completed,
  ])

  useEffect(() => {
    if (!snapshotHydrated) return

    const enabled =
      localStorage.getItem(
        'promanity_ambience'
      ) === 'true'

    setAmbienceEnabled(enabled)
  }, [snapshotHydrated])

  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const { guard } = useFunnelGate()
  const {
      guard: operationalGuard,
  } = useOperationalCapacityGate()
  const { play, playing } = useVoicePlayer()
  const synSpeech = useSynSpeech()

  const canBypassPhaseLocks = Boolean(isOwner)
  const rawPhase2Enabled =
    dashboard.phase2Enabled ??
    dashboard.phase_2_enabled ??
    dashboard.promanityPhase2Enabled ??
    dashboard.promanity_phase_2_enabled ??
    false
  const phase2Enabled =
    rawPhase2Enabled === true ||
    rawPhase2Enabled === 1 ||
    rawPhase2Enabled === '1' ||
    rawPhase2Enabled === 'true'
  const isPhase1Locked = !canBypassPhaseLocks && !phase2Enabled

  const initialPortalUrl =
    dashboard.promanityPortalUrl ||
    dashboard.promanity_portal_url ||
    dashboard.portalUrl ||
    null
  const phase1CountdownEndsAt =
    dashboard.phase1CountdownEndsAt ||
    dashboard.phase_1_countdown_ends_at ||
    null
  const hasPhase1CountdownDate = Boolean(String(phase1CountdownEndsAt || '').trim())

  const [portalUrl, setPortalUrl] = useState<string | null>(initialPortalUrl)
  const [portalInstalling, setPortalInstalling] = useState(false)
  const [portalError, setPortalError] = useState<string | null>(null)
  const [portalCopied, setPortalCopied] = useState(false)
  const [activationLoading, setActivationLoading] = useState(false)
  const [activationAllowed, setActivationAllowed] = useState(false)
  const [activationReason, setActivationReason] = useState<string | null>(null)
  const [walletLoading, setWalletLoading] = useState(false)
  const [walletAddress, setWalletAddress] = useState<string | null>(null)
  const [phase1Countdown, setPhase1Countdown] = useState(() => getCountdownParts(phase1CountdownEndsAt))
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [credits, setCredits] = useState(
    Number(dashboard.credits || 0)
  )
  const promanium =
    dashboard.promanium ?? {
      includedAvailable: 0,
      includedLimit: 0,
      extraAvailable: 0,
      totalAvailable: 0,
      activeCapabilities: 0,
    }
  const [summary, setSummary] = useState<SummaryPayload | null>(null)
  const [focusContent, setFocusContent] = useState<PromanityFocusContent | null>(null)
  const [activeRouteIndex, setActiveRouteIndex] = useState(1)
  const [motherVideoPlaying, setMotherVideoPlaying] = useState(false)

  const savedActivationRoute =
    snapshot.activationRoute ||
    snapshot.activation_route ||
    null
  const welcomeCompleted =
    Boolean(
      snapshot.welcomeCompleted
    ) ||
    Boolean(
      snapshot.welcome_completed
    ) ||
    Boolean(
      snapshot.flags
        ?.welcome_completed
    )
  const savedAnimatedStepIds =
    Array.isArray(
      snapshot.ui?.animatedStepIds
    )
      ? snapshot.ui
          ?.animatedStepIds || []
      : Array.isArray(
            snapshot.animatedStepIds
          )
        ? snapshot.animatedStepIds
        : []

  const [experienceStarted, setExperienceStarted] = useState(Boolean(savedActivationRoute))
  const [activationRoute, setActivationRoute] = useState<ActivationRouteId | null>(savedActivationRoute)
  const [showMissionProtocol, setShowMissionProtocol] = useState(welcomeCompleted)
  const [animatedStepIds, setAnimatedStepIds] = useState<string[]>(savedAnimatedStepIds)

  const motherRef = useRef<MotherModuleHandle | null>(null)
  const motherStartedRef = useRef(false)
  const portalPublicUrl = portalUrl || initialPortalUrl

  const ambassadorLicenseStep: NextStep = {
    id: 'ambassador-license',
    title: 'Activar Licencia de Embajador',
    description: activationAllowed
      ? 'Tu licencia está activa. Ya puedes usar tu portal para invitar.'
      : 'Activa tu licencia para desbloquear tu link de invitados y comenzar tu operación.',
    done: Boolean(activationAllowed),
    ctaLabel: activationAllowed ? 'Licencia activa' : 'Activar licencia',
    actionKey: 'ambassador-license',
    videoSrc: '/video/onboarding/6101/ambassador-license.mp4',
    videoPoster: '/images/aia/steps/ambassador-license-poster.jpg',
  }

  const walletStep: NextStep = {
    id: 'wallet-setup',
    title: 'Configurar Wallet de ganancias',
    description: walletAddress
      ? 'Tu wallet ya está conectada para recibir ganancias.'
      : 'Conecta la wallet donde recibirás tus ganancias de embajador.',
    done: Boolean(walletAddress),
    ctaLabel: walletAddress ? 'Ver wallet' : 'Configurar wallet',
    actionKey: 'wallet-setup',
    videoSrc: '/video/onboarding/6101/wallet-setup.mp4',
    videoPoster: '/images/aia/steps/wallet-setup-poster.jpg',
  }

  const portalStep: NextStep = {
    id: 'promanity-portal',
    title: 'Activar Link de Invitados',
    description: portalPublicUrl
      ? 'Tu link de invitados está listo para compartir.'
      : 'Instala tu Portal Promanity y genera tu link personal de invitados.',
    done: Boolean(portalPublicUrl),
    ctaLabel: portalPublicUrl ? 'Copiar link' : 'Activar link',
    actionKey: 'promanity-portal',
    videoSrc: '/video/onboarding/6101/promanity-portal.mp4',
    videoPoster: '/images/aia/steps/promanity-portal-poster.jpg',
  }

  const effectiveSteps = useMemo(
    () =>
      buildOnboardingSteps({
        dashboard,

        initialJourney:
          genesisProfile?.initialJourney,

        activationAllowed,

        walletAddress,

        portalUrl: portalPublicUrl,

        canBypassPhaseLocks,

        phase2Enabled,

        ambassadorLicenseStep,

        walletStep,

        portalStep,
      }),
    [
      dashboard,

      genesisProfile?.initialJourney,

      activationAllowed,

      walletAddress,

      portalPublicUrl,

      canBypassPhaseLocks,

      phase2Enabled,

      ambassadorLicenseStep,

      walletStep,

      portalStep,
    ]
  )

  useEffect(() => {
    if (savedAnimatedStepIds.length > 0 || !welcomeCompleted) return
    setAnimatedStepIds(
      effectiveSteps
        .filter((step) => !PHASE_1_STEP_IDS.includes(step.id as (typeof PHASE_1_STEP_IDS)[number]))
        .map((step) => step.id)
    )
  }, [effectiveSteps, savedAnimatedStepIds.length, welcomeCompleted])

  const completedSteps = effectiveSteps.filter((step) => step.done).length
  const totalSteps = effectiveSteps.length
  const coreEnergy = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0
  const activeOnboardingStep = useMemo(() => {
    return (
      effectiveSteps.find(
        (step) => !step.done && !step.locked && !step.phaseLocked
      ) || null
    )
  }, [effectiveSteps])

  const hasStrategic = dashboard.nextSteps.some((step) => step.id === 'strategic-profile' && step.done)
  const hasKnowledge = dashboard.nextSteps.some((step) => step.id === 'knowledge-base' && step.done)
  const hasIdentity = dashboard.nextSteps.some((step) => step.id === 'assistant-identity' && step.done)
  const hasOperationalDirectives =  dashboard.capabilities?.operationalDirectives ?? dashboard.nextSteps.some((step) => step.id === 'operational-directives' && step.done)
  const hasAvatar = dashboard.nextSteps.some((step) => step.id === 'avatar-identity' && step.done)
  const effectiveActivationRoute = activationRoute || savedActivationRoute
  const creationMode = effectiveActivationRoute
    ? mapActivationRouteToCreationMode(effectiveActivationRoute)
    : dashboard.strategicPrefill?.creationMode || ''
  const isOnboarding =
    !hasAvatar ||
    !hasStrategic ||
    (creationMode === 'product_service' && !hasKnowledge) ||
    !hasOperationalDirectives
  const isRouteSelectionPhase =
    snapshotHydrated &&
    isOnboarding &&
    !activationRoute &&
    !experienceStarted

  const shouldShowStepVideo =
    snapshotHydrated &&
    isOnboarding &&
    welcomeCompleted &&
    Boolean(
      activeOnboardingStep?.videoSrc
    )

  const enableAmbience =
    useCallback(()=>{

        localStorage.setItem(
            'promanity_ambience_muted',
            'false'
        )

        setAmbienceEnabled(true)

        setAmbienceBlocked(false)

        setUnlockRequestId(v=>v+1)

    },[])

  const phase1Steps = effectiveSteps.filter((step) =>
    PHASE_1_STEP_IDS.includes(step.id as (typeof PHASE_1_STEP_IDS)[number])
  )
  const phase2Steps = effectiveSteps.filter(
    (step) => !PHASE_1_STEP_IDS.includes(step.id as (typeof PHASE_1_STEP_IDS)[number])
  )
  const pendingSteps = effectiveSteps.filter((step) => !step.done && !step.locked && step.focusContent)
  const firstPendingStep = pendingSteps[0] || null

  const WELCOME_EXPERIENCE_MODE: WelcomeExperienceMode =
    (process.env.NEXT_PUBLIC_PROMANITY_WELCOME_MODE as WelcomeExperienceMode) ||
    'video'

  const shouldShowWelcomeExperience =
    snapshotHydrated &&
    !welcomeCompleted &&
    WELCOME_EXPERIENCE_MODE !== 'none'

  const shouldShowWelcomeVideo =
    shouldShowWelcomeExperience &&
    WELCOME_EXPERIENCE_MODE === 'video'

  const shouldPlayWelcomeAudio =
    shouldShowWelcomeExperience &&
    WELCOME_EXPERIENCE_MODE === 'audio'

  const shouldShowWelcomeSequence =
    shouldShowWelcomeExperience &&
    WELCOME_EXPERIENCE_MODE === 'sequence'

  const AIA_WELCOME_VIDEO_SRC = isPhase1Locked
    ? '/video/promanity/aia-welcome-phase-01.mp4'
    : '/video/promanity/aia-welcome.mp4'
  const AIA_WELCOME_AUDIO_SRC = isPhase1Locked
    ? '/audio/voice/mother/welcome-phase-01.mp3'
    : '/audio/voice/mother/welcome.mp3'
  const currentAiaVideoSrc = shouldShowWelcomeVideo
    ? AIA_WELCOME_VIDEO_SRC
    : activeOnboardingStep?.videoSrc || AIA_WELCOME_VIDEO_SRC
  const currentAiaVideoPoster = shouldShowWelcomeVideo
    ? '/images/promanity/aia-poster.jpg'
    : activeOnboardingStep?.videoPoster || '/images/promanity/aia-poster.jpg'

  const stats = dashboard.stats ?? MOCK_DATA.stats ?? []
  const revenueData = dashboard.revenueData ?? MOCK_DATA.revenueData ?? []
  const salesData = dashboard.salesData ?? MOCK_DATA.salesData ?? []
  const maxRevenue = Math.max(...revenueData.map((point) => point.value), 1)
  const maxSales = Math.max(...salesData.map((point) => point.sales), 1)
  const avatarUrl = profile?.avatar_url?.trim() || dashboard.syn.image?.trim() || userImage?.trim() || null

  const invitedReferrals = Number(
    summary?.directCount ??
      dashboard.referralsInvited ??
      dashboard.invitedReferrals ??
      dashboard.referrals_count ??
      0
  ).toLocaleString('es-MX')
  const affiliateEarnings = Number(summary?.totalEarnings ?? 0).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'USD',
  })

  const activationStatusLabel = activationLoading ? 'Verificando' : activationAllowed ? 'Activo' : 'Pendiente'
  const activationStatusHint = activationLoading
    ? 'Validando tu acceso'
    : activationAllowed
      ? 'Puedes activar tu Portal Promanity'
      : 'Activa tu acceso para instalar tu portal'
  const phaseLabel = isPhase1Locked ? 'Fase 1' : 'Fase 2'

  const motherText = useMemo(() => {
    if (!experienceStarted) return 'Esperando activación de experiencia…'
    if (playing && !hasAvatar) return 'Manifestando avatar de identidad…'
    if (playing && !hasStrategic) return 'Inicializando núcleo estratégico…'
    if (playing && creationMode === 'product_service' && !hasKnowledge) return 'Preparando integración de conocimiento…'
    if (playing && !hasOperationalDirectives) return 'Definiendo directivas operativas de AIA'
    if (isOnboarding) return 'Guiándote a través de la activación completa…'
    return 'Sistema estable. Transferencia al SYN completada.'
  }, [experienceStarted, playing, hasAvatar, hasStrategic, creationMode, hasKnowledge, hasOperationalDirectives, isOnboarding])

  const loadSummary = useCallback(async () => {
    try {
      const res = await fetch('/api/affiliate/downline/summary', { cache: 'no-store' })
      if (!res.ok) throw new Error('Failed to load summary')
      const json = (await res.json()) as Record<string, unknown>
      setSummary({
        directCount: Number(json.directCount ?? json.direct_count ?? 0),
        totalNetwork: Number(json.totalNetwork ?? json.total_network ?? 0),
        totalEarnings: Number(json.totalEarnings ?? json.total_earnings ?? 0),
        deepestLevel: Number(json.deepestLevel ?? json.deepest_level ?? 0),
      })
    } catch {
      setSummary({ directCount: 0, totalNetwork: 0, totalEarnings: 0, deepestLevel: 0 })
    }
  }, [])

  const setAIAView =
    useCallback(
      (view: AIAView) => {
        if (
          currentScene !== 'console' ||
          view === aiaView
        ) {
          return
        }

        setRoomTransition(true)

        window.setTimeout(() => {
          setAIAViewState(view)

          window.setTimeout(() => {
            setRoomTransition(false)
          }, 650)
        }, 120)
      },
      [
        currentScene,
        aiaView,
      ]
    )

  const openFocus = useCallback(
    (content: Exclude<PromanityFocusContent, null>) => {
        setFocusContent(content)
        setAIAView('focus')
    }, [setAIAView])

  const closeFocus = useCallback(() => {
      setFocusContent(null)
  }, [setAIAView])

  const refreshAfterFocusSave =
  useCallback(
    () => {
      closeFocus()
      router.refresh()
    },
    [
      closeFocus,
      router,
    ]
  )

  const goToNextStepAfterStrategicSave =
  useCallback(
    (_payload: StrategicProfileSaveResult) => {
      closeFocus()
      router.refresh()
    },
    [
      closeFocus,
      router,
    ]
  )

  const copyPortalLink = useCallback(async (url: string) => {
    await navigator.clipboard.writeText(url)
    setPortalCopied(true)
    window.setTimeout(() => setPortalCopied(false), 1200)
  }, [])

  const installPromanityPortal = useCallback(async () => {
    setPortalInstalling(true)
    try {
      const res = await fetch('/api/promanity/portal/install', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sellerId: Number(dashboard.sellerId || 0),
          userId: Number(dashboard.userId || 0),
        }),
      })
      const payload = (await res.json().catch(() => null)) as { publicUrl?: string; error?: string } | null
      if (!res.ok || !payload?.publicUrl) throw new Error(payload?.error || 'No se pudo activar el portal')
      setPortalUrl(payload.publicUrl)
      await copyPortalLink(payload.publicUrl)
      router.refresh()
    } catch (error) {
      console.error('[usePromanityExperience] portal install failed:', error)
      setPortalError(error instanceof Error ? error.message : 'No se pudo activar el portal')
    } finally {
      setPortalInstalling(false)
    }
  }, [copyPortalLink, dashboard.sellerId, dashboard.userId, router])

  const handlePromanityPortalAction = useCallback(async () => {
    setPortalError(null)
    if (portalPublicUrl) {
      await copyPortalLink(portalPublicUrl)
      return
    }
    await guard(() => installPromanityPortal(), { title: 'Activa tu Portal Promanity' })
  }, [copyPortalLink, guard, installPromanityPortal, portalPublicUrl])

  const handleActivateAccount = useCallback(async () => {
    await guard(async () => {}, { title: 'Activa tu acceso para crear tu Portal Promanity' })
  }, [guard])

  const handleStepAction = useCallback(
    (step?: NextStep | null) => {
      if (!step || step.locked || step.phaseLocked) return
      if (step.actionKey === 'ambassador-license') {
        if (!activationAllowed) void handleActivateAccount()
        return
      }
      if (step.actionKey === 'wallet-setup') {
        if (activationAllowed) router.push('/settings/wallet')
        return
      }
      if (step.actionKey === 'promanity-portal') {
        if (activationAllowed) void handlePromanityPortalAction()
        return
      }
      if (step.focusContent) {
        openFocus(step.focusContent)
        return
      }
      if (step.ctaHref) router.push(step.ctaHref)
    },
    [activationAllowed, handleActivateAccount, handlePromanityPortalAction, openFocus, router]
  )

  const isStepActionDisabled = useCallback(
    (step?: NextStep | null) => {
      if (!step || step.locked || step.phaseLocked) return true
      if (step.actionKey === 'ambassador-license') return activationAllowed
      if (step.actionKey === 'wallet-setup') return !activationAllowed
      if (step.actionKey === 'promanity-portal') return !activationAllowed || portalInstalling
      return false
    },
    [activationAllowed, portalInstalling]
  )

  const persistSnapshot = useCallback(
    async (
      patch: Partial<PromanitySnapshot>
    ) => {
      const sellerId = Number(
        dashboard.sellerId || 0
      )

      const userId = Number(
        dashboard.userId || 0
      )

      if (!sellerId || !userId) {
        throw new Error(
          'Missing sellerId or userId'
        )
      }

      /*
      * Calculamos el siguiente snapshot usando
      * siempre la referencia más reciente.
      */
      const nextSnapshot =
        mergeSnapshot<PromanitySnapshot>(
          snapshotRef.current,
          patch
        )

      /*
      * Actualización optimista local.
      */
      snapshotRef.current =
        nextSnapshot

      setSnapshot(nextSnapshot)

      /*
      * Serializamos las escrituras para impedir
      * que una respuesta antigua sobrescriba
      * una escritura más reciente.
      */
      const write =
        snapshotWriteQueue.current.then(
          async () => {
            const res = await fetch(
              '/api/dashboard/snapshot',
              {
                method: 'POST',

                headers: {
                  'Content-Type':
                    'application/json',
                },

                body: JSON.stringify({
                  sellerId,
                  userId,
                  snapshot:
                    nextSnapshot,
                }),
              }
            )

            const json = await res
              .json()
              .catch(() => null)

            if (!res.ok || !json?.ok) {
              throw new Error(
                json?.error ||
                  'No se pudo guardar el snapshot'
              )
            }

            return nextSnapshot
          }
        )

      snapshotWriteQueue.current =
        write.catch(() => undefined)

      try {
        return await write
      } catch (error) {
        console.error(
          '[usePromanityExperience] snapshot persistence failed:',
          error
        )

        throw error
      }
    },
    [
      dashboard.sellerId,
      dashboard.userId,
    ]
  )

  const expectedConsoleView: AIAView =
    lifecycle.consoleOpened
      ? 'focus'
      : 'screen'

  const lookAt = useCallback(
    (scene: RoomScene) => {
      const alreadyAtExpectedScene =
        scene === currentScene &&
        (
          scene !== 'console' ||
          aiaView === expectedConsoleView
        )

      if (alreadyAtExpectedScene) {
        return
      }

      setRoomTransition(true)

      window.setTimeout(() => {
        if (scene === 'console') {
          setFocusContent(null)
          setAIAViewState(expectedConsoleView)
        }

        setCurrentScene(scene)

        window.setTimeout(() => {
          setRoomTransition(false)
        }, 650)
      }, 220)
    },
    [
      currentScene,
      aiaView,
      expectedConsoleView,
    ]
  )

  const backToOverview =
    useCallback(() => {
      lookAt('overview')
    }, [lookAt])

  const setRoomPhase =
    useCallback(
      (phase: PromanityPhase) => {
        setLifecycle(
          (previous) => ({
            ...previous,
            phase,
          })
        )
      },
      []
    )

  const updateRoomLifecycle =
    useCallback(
      (
        patch: Partial<RoomLifecycleState>
      ) => {
        setLifecycle(
          (previous) => ({
            ...previous,
            ...patch,

            narrator:
              patch.narrator
                ? {
                    ...previous.narrator,
                    ...patch.narrator,
                  }
                : previous.narrator,
          })
        )
      },
      []
    )  

  const enterCoreConsole =
    useCallback(() => {
      /*
      * El snapshot persistido también cuenta como fuente de verdad.
      * Esto evita reiniciar el Core aunque otro estado local todavía
      * no haya terminado de sincronizarse.
      */
      const coreAlreadyActive =
        lifecycle.phase === 'core_active' ||
        Boolean(snapshotRef.current.flags?.core_active) ||
        Boolean(snapshotRef.current.flags?.core_initialized)

      if (!coreAlreadyActive) {
        updateRoomLifecycle({
          phase: 'aia_boot',
        })
      }

      lookAt('console')
    }, [
      lifecycle.phase,
      lookAt,
      updateRoomLifecycle,
    ])

  const narratorSpeak =
    useCallback(
      (
        speaker:
          | 'foundation'
          | 'aia',
        message: string
      ) => {
        console.log(
          '[Narrator]',
          speaker,
          message
        )

        setLifecycle(
          (previous) => ({
            ...previous,

            narrator: {
              speaker,
              speaking: true,
              message,
            },
          })
        )
      },
      []
    )

  const narratorSilence =
    useCallback(() => {
      setLifecycle(
        (previous) => ({
          ...previous,

          narrator: {
            speaker: null,
            speaking: false,
            message: null,
          },
        })
      )
  }, [])

  const persistRoomState =
    useCallback(
      async ({
        lifecyclePatch,
        scene,
        nextAiaView,
        snapshotPatch,
      }: {
        lifecyclePatch?: Partial<RoomLifecycleState>
        scene?: RoomScene
        nextAiaView?: AIAView
        snapshotPatch?: Partial<PromanitySnapshot>
      }) => {
        const nextLifecycle = {
          ...lifecycle,
          ...(lifecyclePatch || {}),

          narrator: lifecycle.narrator,
        }

        if (lifecyclePatch) {
          updateRoomLifecycle(
            lifecyclePatch
          )
        }

        if (scene) {
          setCurrentScene(scene)
        }

        if (nextAiaView) {
          setAIAViewState(
            nextAiaView
          )
        }

        return persistSnapshot({
          ...(snapshotPatch || {}),

          phase:
            nextLifecycle.phase,

          room: {
            currentScene:
              scene ||
              currentScene,

            aiaView:
              nextAiaView ||
              aiaView,

            lifecycle: {
              ...nextLifecycle,

              /*
              * La narración es temporal.
              * No debe restaurarse hablando.
              */
              narrator: {
                speaker: null,
                speaking: false,
                message: null,
              },
            },
          },
        })
      },
      [
        aiaView,
        currentScene,
        lifecycle,
        persistSnapshot,
        updateRoomLifecycle,
      ]
  )

  const completeConsoleEntry =
    useCallback(async () => {
      await persistRoomState({
        lifecyclePatch: {
          consoleOpened: true,
        },

        snapshotPatch: {
          mode: 'promanity_room',

          room: {
            lifecycle: {
              consoleOpened: true,
            },
          },
        },
      })
    }, [persistRoomState])

  const completeGenesis =
  useCallback(
    async (
      profile: GenesisProfile
    ) => {
      const sellerId = Number(
        dashboard.sellerId || 0
      )

      const userId = Number(
        dashboard.userId || 0
      )

      if (!sellerId || !userId) {
        throw new Error(
          'Missing sellerId or userId'
        )
      }

      const completedAt =
        profile.completedAt ||
        new Date().toISOString()

      const startedAt =
        profile.startedAt ||
        lifecycle.genesisStartedAt ||
        completedAt

      const genesis: GenesisProfile = {
        ...profile,

        completed: true,

        startedAt,
        completedAt,
      }

      /*
       * Genesis se guarda únicamente como
       * conocimiento estratégico del usuario.
       */
      const strategicRes =
        await fetch(
          '/api/ai/user/strategic-profile/save',
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',
            },

            body: JSON.stringify({
              sellerId,
              userId,

              source:
                'promanity_genesis',

              merge: {
                genesis,
              },
            }),
          }
        )

      const strategicJson =
        await strategicRes
          .json()
          .catch(() => null)

      if (
        !strategicRes.ok ||
        !strategicJson?.ok
      ) {
        throw new Error(
          strategicJson?.error ||
            'No se pudo guardar Genesis en el perfil estratégico'
        )
      }

      /*
       * Actualización local inmediata.
       *
       * No esperamos a que el servidor vuelva
       * a enviar strategicPrefill.
       */
      setGenesisProfile(genesis)

      /*
       * El snapshot únicamente conserva
       * el estado operativo de la habitación.
       *
       * No guarda respuestas, scores,
       * afinidades ni initialJourney.
       */
      await persistRoomState({
        scene: 'overview',

        lifecyclePatch: {
          phase:
            'core_dormant',

          isFirstSystemEntry:
            false,

          /*
           * Nombre heredado del lifecycle.
           * Ahora significa genesisCompleted.
           */
          hasAcceptedOriginRole:
            true,

          genesisStartedAt:
            startedAt,

          genesisCompletedAt:
            completedAt,

          consoleActivated:
            true,

          consoleOpened:
            false,

          windowActivated:
            false,

          windowOpened:
            true,
        },

        snapshotPatch: {
          mode:
            'promanity_room',

          phase:
            'core_dormant',

          flags: {
            genesis_started:
              true,

            genesis_completed:
              true,
          },
        },
      })

      return {
        ok: true,
        genesis,
      }
    },
    [
      dashboard.sellerId,
      dashboard.userId,
      lifecycle.genesisStartedAt,
      persistRoomState,
    ]
  )

  const completeCoreBoot =
  useCallback(async () => {
    await persistRoomState({
      scene: 'overview',

      lifecyclePatch: {
        phase: 'core_active',

        narrator: {
          speaker: null,
          speaking: false,
          message: null,
        },
      },

      snapshotPatch: {
        mode: 'promanity_room',

        phase: 'core_active',

        flags: {
          core_initialized: true,
          core_active: true,
        },
      },
    })
  }, [persistRoomState])


  const activateAIA =
    useCallback(
      async ({
        capabilityId,
      }: {
        capabilityId:
          OperationalCapabilityId
      }) => {
        await operationalGuard(
          async () => {
            setActivationLoading(
              true
            )

            try {
              const response =
                await fetch(
                  '/api/promanity/aia/activate',
                  {
                    method:
                      'POST',

                    headers: {
                      'Content-Type':
                        'application/json',
                    },

                    body:
                      JSON.stringify({
                        sellerId:
                          Number(
                            dashboard
                              .sellerId ||
                            0
                          ),

                        capabilityId,
                      }),
                  }
                )

              const json =
                await response
                  .json()
                  .catch(
                    () => null
                  )

              if (
                !response.ok ||
                !json?.ok
              ) {
                throw new Error(
                  json?.error ||
                    'No se pudo activar AIA'
                )
              }

              if (
                Number.isFinite(
                  Number(
                    json.credits
                  )
                )
              ) {
                setCredits(
                  Number(
                    json.credits
                  )
                )
              }

              /*
              * Ya tienes esta función:
              * guarda phase=core_active,
              * core_initialized=true y
              * core_active=true en el snapshot.
              */
              await completeCoreBoot()

              router.refresh()
            } catch (error) {
              console.error(
                '[usePromanityExperience] activate AIA failed:',
                error
              )

              throw error
            } finally {
              setActivationLoading(
                false
              )
            }
          },
          {
            capabilityId,

            title:
              'Activa la capacidad operativa de AIA',
          }
        )
      },
      [
        operationalGuard,
        dashboard.sellerId,
        completeCoreBoot,
        router,
      ]
    )

  const saveActivationRoute =
  useCallback(
    async (
      route: ActivationRouteId
    ) => {
      const creationModeForRoute =
        mapActivationRouteToCreationMode(
          route
        )

      const patch: Partial<PromanitySnapshot> = {
        mode:
          'promanity_activation',

        phase:
          'route_selected',

        activation_route:
          route,

        activationRoute:
          route,

        creationMode:
          creationModeForRoute,

        calibration: {
          creationMode:
            creationModeForRoute,

          activation_route:
            route,

          activationRoute:
            route,
        },

        flags: {
          route_selected:
            true,

          greeted:
            false,
        },
      }

      await persistSnapshot(patch)

      return mergeSnapshot(
        snapshotRef.current,
        patch
      )
    },
    [persistSnapshot]
  )

  const saveWelcomeExperienceCompleted =
  useCallback(
    async (
      nextOpen?: string | null
    ) => {
      if (!activationRoute) {
        return null
      }

      const creationModeForRoute =
        mapActivationRouteToCreationMode(
          activationRoute
        )

      const revealedStepIds =
        effectiveSteps.map(
          (step) => step.id
        )

      const patch: Partial<PromanitySnapshot> = {
        mode:
          'promanity_activation',

        phase:
          'welcome_completed',

        activation_route:
          activationRoute,

        activationRoute,

        creationMode:
          creationModeForRoute,

        calibration: {
          creationMode:
            creationModeForRoute,

          activation_route:
            activationRoute,

          activationRoute,
        },

        flags: {
          route_selected:
            true,

          greeted:
            true,

          welcome_completed:
            true,

          mission_protocol_revealed:
            true,
        },

        ui: {
          showMissionProtocol:
            true,

          animatedStepIds:
            revealedStepIds,

          nextOpen:
            nextOpen || null,
        },
      }

      await persistSnapshot(patch)

      return mergeSnapshot(
        snapshotRef.current,
        patch
      )
    },
    [
      activationRoute,
      effectiveSteps,
      persistSnapshot,
    ]
  )

  const completeWelcomeExperience = useCallback(async () => {
    if (welcomeCompleted) return

    setAnimatedStepIds(
      effectiveSteps.map((step) => step.id)
    )

    setShowMissionProtocol(true)

    const nextStep =
      effectiveSteps.find(
        (step) =>
          !step.done &&
          !step.locked &&
          !step.phaseLocked
      ) || null

    try {
      await saveWelcomeExperienceCompleted()

      router.refresh()
    } catch (error) {
      console.error(
        '[usePromanityExperience] complete welcome failed:',
        error
      )
    }
  }, [
    effectiveSteps,
    router,
    saveWelcomeExperienceCompleted,
    welcomeCompleted,
  ])

  const handleSelectActivationRoute = useCallback(
    async (route: ActivationRouteId) => {
      setActivationRoute(route)
      closeFocus()
      setShowMissionProtocol(false)
      setAnimatedStepIds([])
      motherStartedRef.current = false
      try {
        await saveActivationRoute(route)
      } catch (error) {
        console.error('[usePromanityExperience] save activation route failed:', error)
      }
      setExperienceStarted(true)
    },
    [saveActivationRoute]
  )

  const playWelcomeExperience = useCallback(async () => {
    if (WELCOME_EXPERIENCE_MODE === 'video') {
      try {
        setMotherVideoPlaying(true)
        await motherRef.current?.playUntilEnd()
      } finally {
        setMotherVideoPlaying(false)
      }

      return
    }

    if (WELCOME_EXPERIENCE_MODE === 'audio') {
      await play(AIA_WELCOME_AUDIO_SRC)
      return
    }

    /*
    * Las modalidades "sequence" y "none" no se reproducen aquí.
    *
    * La secuencia visual notificará su finalización mediante
    * completeWelcomeExperience().
    */
  }, [AIA_WELCOME_AUDIO_SRC, play])

  const getWelcomeDurationMsSafe = useCallback(async () => {
    if (WELCOME_EXPERIENCE_MODE === 'audio') {
      return 11_000
    }

    if (WELCOME_EXPERIENCE_MODE !== 'video') {
      return 0
    }

    const initial =
      motherRef.current?.getDurationMs() || 0

    if (initial > 0) {
      return initial
    }

    await wait(250)

    const retry =
      motherRef.current?.getDurationMs() || 0

    return retry > 0 ? retry : 11_000
  }, [])

  const handleSynWake = useCallback(async () => {
    await synSpeech.speak({
      voice: 'syn_default',
      mode: 'realtime_conversation',
      provider: 'gemini',
      text: 'Tu SYN ya está listo para operar. Detecté que quieres enfocarte en crear contenido épico, monetizar tu conocimiento y construir una presencia más fuerte con ayuda de inteligencia artificial.',
      instructions: 'Habla en español latino, tono natural, futurista, cercano y seguro.',
    })
  }, [synSpeech])

  const goToActivationCard = useCallback((index: number, cardCount: number) => {
    setActiveRouteIndex((index + cardCount) % cardCount)
  }, [])
  const goToNextActivationCard = useCallback((cardCount: number) => {
    setActiveRouteIndex((previous) => (previous + 1) % cardCount)
  }, [])
  const goToPrevActivationCard = useCallback((cardCount: number) => {
    setActiveRouteIndex((previous) => (previous - 1 + cardCount) % cardCount)
  }, [])

  useEffect(() => {
    setPhase1Countdown(getCountdownParts(phase1CountdownEndsAt))
    if (!phase1CountdownEndsAt) return
    const interval = window.setInterval(() => setPhase1Countdown(getCountdownParts(phase1CountdownEndsAt)), 1000)
    return () => window.clearInterval(interval)
  }, [phase1CountdownEndsAt])

  useEffect(() => {
    document.body.classList.toggle('pm-activation-screen-open', isRouteSelectionPhase)
    document.body.style.overflow = isRouteSelectionPhase ? 'hidden' : ''
    return () => {
      document.body.classList.remove('pm-activation-screen-open')
      document.body.style.overflow = ''
    }
  }, [isRouteSelectionPhase])

  useEffect(() => {
    let cancelled = false
    async function loadActivationStatus() {
      const sellerId = Number(dashboard.sellerId || 0)
      if (!sellerId) {
        setActivationLoading(false)
        setActivationAllowed(false)
        setActivationReason('missing_seller')
        return
      }
      try {
        setActivationLoading(true)
        const res = await fetch(`/api/access/funnels?seller_id=${sellerId}`, { cache: 'no-store' })
        const payload = (await res.json().catch(() => null)) as { allowed?: boolean; reason?: string } | null
        if (cancelled) return
        setActivationAllowed(Boolean(res.ok && payload?.allowed))
        setActivationReason(payload?.reason || null)
      } catch (error) {
        if (cancelled) return
        console.error('[usePromanityExperience] activation status failed:', error)
        setActivationAllowed(false)
        setActivationReason('error')
      } finally {
        if (!cancelled) setActivationLoading(false)
      }
    }
    void loadActivationStatus()
    return () => {
      cancelled = true
    }
  }, [dashboard.sellerId])

  useEffect(() => {
    let cancelled = false
    async function loadWallet() {
      const userId = Number(dashboard.userId || 0)
      if (!userId) {
        setWalletAddress(null)
        return
      }
      try {
        setWalletLoading(true)
        const payload = await apiGetWallet(userId)
        const address = extractWalletAddress(payload)
        if (!cancelled) setWalletAddress(address || null)
      } catch (error) {
        if (!cancelled) {
          console.error('[usePromanityExperience] wallet load failed:', error)
          setWalletAddress(null)
        }
      } finally {
        if (!cancelled) setWalletLoading(false)
      }
    }
    void loadWallet()
    return () => {
      cancelled = true
    }
  }, [dashboard.userId])

  useEffect(() => {
    void loadSummary()
  }, [loadSummary])

  useEffect(() => {
    if (!dashboard.userId) return
    let alive = true
    setProfileLoading(true)
    fetch(`/api/user-profile/${dashboard.userId}`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload: UserProfile | { error?: string } | null) => {
        if (!alive) return
        const nextProfile = payload && !('error' in payload) ? payload : null
        setProfile(nextProfile)
        if (typeof nextProfile?.ai_credits === 'number') setCredits(nextProfile.ai_credits)
      })
      .catch(() => {
        if (alive) setProfile(null)
      })
      .finally(() => {
        if (alive) setProfileLoading(false)
      })
    return () => {
      alive = false
    }
  }, [dashboard.userId])

  useEffect(() => {
    /*
    * No se puede decidir si corresponde
    * reproducir la bienvenida hasta haber
    * recuperado el snapshot.
    */
    if (!snapshotHydrated) return

    if (
      !experienceStarted ||
      !isOnboarding
    ) {
      return
    }

    if (welcomeCompleted) {
      motherStartedRef.current = true
      setShowMissionProtocol(true)
      setAnimatedStepIds((previous) =>
        previous.length > 0
          ? previous
          : effectiveSteps.map((step) => step.id)
      )
      return
    }

    // El video visible lo controla AIAWallExperience.
    if (
      WELCOME_EXPERIENCE_MODE === 'video' ||
      WELCOME_EXPERIENCE_MODE === 'sequence' ||
      WELCOME_EXPERIENCE_MODE === 'none'
    ) {
      return
    }

    if (motherStartedRef.current) return
    motherStartedRef.current = true

    let cancelled = false
    const timers: ReturnType<typeof setTimeout>[] = []

    async function runWelcomeSequence() {
      try {
        setShowMissionProtocol(false)
        setAnimatedStepIds(effectiveSteps.filter((step) => step.done).map((step) => step.id))
        setShowMissionProtocol(true)
        const mediaDurationMs = await getWelcomeDurationMsSafe()
        const pendingRevealSteps = effectiveSteps.filter((step) => !step.done)
        const introLeadMs = 11_000
        const outroBufferMs = 2_000
        const usableWindowMs = Math.max(
          mediaDurationMs - introLeadMs - outroBufferMs,
          pendingRevealSteps.length * 900
        )
        const stepSpacingMs = pendingRevealSteps.length > 0
          ? Math.max(Math.floor(usableWindowMs / pendingRevealSteps.length), 900)
          : 900

        pendingRevealSteps.forEach((step, index) => {
          const timer = window.setTimeout(() => {
            if (cancelled) return
            setAnimatedStepIds((previous) => {
              if (previous.includes(step.id)) return previous
              const sfx = new Audio('/sfx/mother/dashboard-step-reveal.mp3')
              sfx.volume = 0.22
              void sfx.play().catch(() => undefined)
              return [...previous, step.id]
            })
          }, introLeadMs + index * stepSpacingMs)
          timers.push(timer)
        })

        await playWelcomeExperience()
        if (cancelled) return
        setAnimatedStepIds(effectiveSteps.map((step) => step.id))
        setShowMissionProtocol(true)
        const nextStep = effectiveSteps.find((step) => !step.done && !step.locked && step.focusContent)
        await saveWelcomeExperienceCompleted()
        if (nextStep?.focusContent) openFocus(nextStep.focusContent)
      } catch (error) {
        console.error('[usePromanityExperience] welcome sequence failed:', error)
      }
    }

    void runWelcomeSequence()
    return () => {
      cancelled = true
      timers.forEach(window.clearTimeout)
    }
    }, [
    effectiveSteps,
    experienceStarted,
    getWelcomeDurationMsSafe,
    isOnboarding,
    openFocus,
    playWelcomeExperience,
    saveWelcomeExperienceCompleted,
    snapshotHydrated,
    welcomeCompleted,
  ])

  const effectiveCreationMode = effectiveActivationRoute
    ? mapActivationRouteToCreationMode(effectiveActivationRoute)
    : dashboard.strategicPrefill?.creationMode || ''
  const strategicInitialData: StrategicPrefill = {
    ...(dashboard.strategicPrefill || {}),
    genesis: genesisProfile || undefined,
    activationRoute: effectiveActivationRoute || undefined,
    activation_route: effectiveActivationRoute || undefined,
    creationMode: effectiveCreationMode || undefined,
  }

  return {
    state: {
      snapshotHydrated,
      dashboard,
      hydrateDashboardData,
      hasServerData,
      profile,
      profileLoading,
      credits,
      summary,
      avatarUrl,
      stats,
      revenueData,
      salesData,
      maxRevenue,
      maxSales,
      invitedReferrals,
      affiliateEarnings,
      experienceStarted,
      isOnboarding,
      isRouteSelectionPhase,
      shouldShowStepVideo,
      showMissionProtocol,
      animatedStepIds,
      activeRouteIndex,
      motherVideoPlaying,
      motherText,
      currentAiaVideoSrc,
      currentAiaVideoPoster,
      firstPendingStep,
      welcomeCompleted,
      welcomeExperienceMode: WELCOME_EXPERIENCE_MODE,
      shouldShowWelcomeExperience,
      shouldShowWelcomeVideo,
      shouldPlayWelcomeAudio,
      shouldShowWelcomeSequence,
    },
    room: {
      state: {
        snapshotHydrated,
        currentScene,
        aiaView,
        promanium,
        transition:
          roomTransition,
        lifecycle,
        snapshot,
      },

      lookAt,
      enterCoreConsole,
      setAIAView,
      backToOverview,

      setTransition:
        setRoomTransition,

      setPhase:
        setRoomPhase,

      updateLifecycle:
        updateRoomLifecycle,

      narratorSpeak,
      narratorSilence,

      persistSnapshot,
      persistRoomState,
      completeCoreBoot,
      completeConsoleEntry,
      completeGenesis,

      openFocus,
      closeFocus,
      focusContent,

      genesis: {
        completed:
          snapshotHydrated &&
          genesisCompleted,

        profile:
          genesisProfile,
      },
    },
    ambience: {
        enabled: ambienceEnabled,
        blocked: ambienceBlocked,
        unlockRequestId,
        setBlocked: setAmbienceBlocked,
        enable: enableAmbience,
    },
    activation: {
      route: activationRoute,
      effectiveRoute: effectiveActivationRoute,
      creationMode,
      loading: activationLoading,
      allowed: activationAllowed,
      reason: activationReason,
      statusLabel: activationStatusLabel,
      statusHint: activationStatusHint,
      selectRoute: handleSelectActivationRoute,
      setExperienceStarted,
      goToCard: goToActivationCard,
      goToNextCard: goToNextActivationCard,
      goToPrevCard: goToPrevActivationCard,
    },
    wallet: {
      loading: walletLoading,
      address: walletAddress,
    },
    portal: {
      url: portalPublicUrl,
      installing: portalInstalling,
      error: portalError,
      copied: portalCopied,
      activateOrCopy: handlePromanityPortalAction,
    },
    phase: {
      phase2Enabled,
      isPhase1Locked,
      canBypassPhaseLocks,
      label: phaseLabel,
      countdownEndsAt: phase1CountdownEndsAt,
      hasCountdownDate: hasPhase1CountdownDate,
      countdown: phase1Countdown,
    },
    protocols: {
      steps: effectiveSteps,
      activeStep: activeOnboardingStep,
      completedSteps,
      totalSteps,
      coreEnergy,
      phase1StepIds: PHASE_1_STEP_IDS,
      phase1Steps,
      phase2Steps,
      open: openFocus,
      onStepAction: handleStepAction,
      onStepVideoAction: handleStepAction,
      isStepActionDisabled,
      isStepVideoActionDisabled: isStepActionDisabled,
    },
    avatar: {
      completed: hasAvatar,
      url: avatarUrl,
    },
    identity: {
      strategicCompleted: hasStrategic,
      OperationalDirectivesCompleted: hasOperationalDirectives,
      creationMode,
      strategicInitialData,
    },
    knowledge: {
      completed: hasKnowledge,
      required: creationMode === 'product_service',
    },
    focus: {
        content: focusContent,

        open: openFocus,

        close: closeFocus,

        onStrategicSaved:
            goToNextStepAfterStrategicSave,

        onSaved:
            refreshAfterFocusSave,
    },
    audio: {
      motherRef,
      playing,
      synSpeech,
      wakeSyn: handleSynWake,
    },
    actions: {
      activateAIA,
      activateAccount: handleActivateAccount,
      deployTransmission: onDeployTransmission,
      connectChannel: onConnectChannel,
      completeWelcomeExperience,
    },
  }
}

export type PromanityExperience = ReturnType<typeof usePromanityExperience>